import React, { useState } from 'react';
import Row from 'containers/Reports/row';
import { connect } from 'react-redux';
import { runAssessmentByQuestionTypeReport } from 'actions/reports';
import Select from 'react-select';
import { FormGroup, NumericInput } from '@blueprintjs/core';
import {
  fetchAllQuestionTypes,
  fetchAllDepartments,
  fetchAllRequestStatus,
} from 'actions/utils';
import { DatePicker } from '@blueprintjs/datetime';
import moment from 'moment';
import { getMaxDate } from 'utils/functions';

const riskScoreOptions = [
  { label: 'Greater than', value: 'ABOVE' },
  { label: 'Less than', value: 'UNDER' },
  { label: 'Equal to', value: 'ON' },
];

const AssessmentByQuestionTypeBase = ({
  runAssessmentByQuestionTypeReport,
  report,
  types,
  departments,
  requestStatus,
  fetchAllQuestionTypes,
  fetchAllDepartments,
  fetchAllRequestStatus,
  isLoadedTypes,
  isLoadedDepartments,
  isLoadedStatus,
  ...rest
}) => {
  const [selectedRenewalDate, setRenewalDate] = useState(null);
  const [selectedApprovalDate, setApprovalDate] = useState(null);
  const [selectedType, setType] = useState(null);
  const [selectedDepartment, setDepartment] = useState(null);
  const [selectedRequestingDepartment, setRequestingDepartment] = useState(
    null
  );
  const [selectedStatus, setStatus] = useState(null);
  const [selectedRisk, setRisk] = useState(riskScoreOptions[0]);
  const [selectedScore, setScore] = useState(0);

  const handleChange = (selected, setter) => {
    setter(selected);
  };

  return (
    <Row
      name="Assessment By Question Type"
      description="Assessments by question type (e.g. BCM, Device/Software)."
      drawer={
        <div style={{ width: '75%' }}>
          <FormGroup label="Question Type" labelInfo="(required)">
            <Select
              options={types}
              getOptionLabel={option => option.typeName}
              getOptionValue={option => option.typeID}
              value={selectedType}
              onChange={selected => handleChange(selected, setType)}
            />
          </FormGroup>
          <FormGroup label="Sponsor Department">
            <Select
              options={departments}
              getOptionLabel={option => option.departmentName}
              getOptionValue={option => option.departmentID}
              value={selectedDepartment}
              onChange={selected => handleChange(selected, setDepartment)}
            />
          </FormGroup>
          <FormGroup label="Requesting Department">
            <Select
              options={departments}
              getOptionLabel={option => option.departmentName}
              getOptionValue={option => option.departmentID}
              value={selectedRequestingDepartment}
              onChange={selected =>
                handleChange(selected, setRequestingDepartment)
              }
            />
          </FormGroup>
          <FormGroup label="Assessment Status">
            <Select
              options={requestStatus}
              getOptionLabel={option => option.statusName}
              getOptionValue={option => option.statusName}
              value={selectedStatus}
              onChange={selected => handleChange(selected, setStatus)}
            />
          </FormGroup>

          <FormGroup label="Risk Score">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ flexBasis: '60%' }}>
                <Select
                  options={riskScoreOptions}
                  value={selectedRisk}
                  onChange={selected => handleChange(selected, setRisk)}
                />
              </div>
              <div style={{ flexBasis: '30%' }}>
                <NumericInput
                  min={0}
                  value={selectedScore}
                  onValueChange={selected => handleChange(selected, setScore)}
                  large
                  fill
                />
              </div>
            </div>
          </FormGroup>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <FormGroup label="Approval Date On or After">
              <DatePicker
                value={selectedApprovalDate}
                onChange={selected => handleChange(selected, setApprovalDate)}
                maxDate={getMaxDate()}
              />
            </FormGroup>
            <FormGroup label="Renewal Date On or After">
              <DatePicker
                value={selectedRenewalDate}
                onChange={selected => handleChange(selected, setRenewalDate)}
                maxDate={getMaxDate()}
              />
            </FormGroup>
          </div>
        </div>
      }
      runReport={() =>
        runAssessmentByQuestionTypeReport({
          questionType: selectedType && selectedType.typeID,
          sponsorDepartment:
            selectedDepartment && selectedDepartment.departmentName,
          departmentsRequesting:
            selectedRequestingDepartment &&
            selectedRequestingDepartment.departmentName,
          requestStatus: selectedStatus && selectedStatus.statusName,
          ...(selectedScore && {
            score: {
              score: selectedScore,
              direction: selectedRisk.value,
            },
          }),
          ...(moment(selectedRenewalDate).isValid() && {
            renewalDate: {
              date: moment(selectedRenewalDate).format('YYYY-MM-DD'),
              direction: 'AFTER',
            },
          }),
          ...(moment(selectedApprovalDate).isValid() && {
            approvalDate: {
              date: moment(selectedApprovalDate).format('YYYY-MM-DD'),
              direction: 'AFTER',
            },
          }),
        })
      }
      data={report}
      disableRunReport={!!!selectedType}
      headers={[
        { label: 'Assessment ID', key: 'vendorRequestID' },
        {
          label: 'Third Party Name',
          key: 'vendorName',
        },
        { label: 'Product / Service', key: 'productServiceName' },
        { label: 'Status', key: 'requestStatus' },
        { label: 'Question Types', key: 'questionTypes' },
        { label: 'Approval Date', key: 'approvalDate' },
        { label: 'Renewal Date', key: 'renewalDate' },
        { label: 'Sponsor Name', key: 'sponsorName' },
        { label: 'Department', key: 'sponsorDepartment' },
        { label: 'Requesting Department', key: 'departmentsRequesting' },
      ]}
      resetDrawerState={() => {
        setRenewalDate(null);
        setApprovalDate(null);
        setType(null);
        setDepartment(null);
        setRequestingDepartment(null);
        setStatus(null);
        setRisk(riskScoreOptions[0]);
        setScore(0);
      }}
      fetchReportFieldsData={() => {
        if (!isLoadedTypes) {
          fetchAllQuestionTypes();
        }
        if (!isLoadedDepartments) {
          fetchAllDepartments();
        }
        if (!isLoadedStatus) {
          fetchAllRequestStatus();
        }
      }}
      {...rest}
    />
  );
};

const mapStateToProps = state => ({
  report: state.reports.assessmentByQuestionType,
  isFetchingReport: state.reports.isFetching,
  isLoadedReport: state.reports.isLoaded,
  errorMessage: state.reports.errorMessage,
  types: state.utils.questionTypes,
  departments: state.utils.departments,
  requestStatus: state.utils.requestStatus,
  isLoadedTypes: state.utils.isLoadedTypes,
  isLoadedStatus: state.utils.isLoadedRequestStatus,
  isLoadedDepartments: state.utils.isLoadedDepartments,
});

const AssessmentByQuestionType = connect(
  mapStateToProps,
  {
    runAssessmentByQuestionTypeReport,
    fetchAllDepartments,
    fetchAllQuestionTypes,
    fetchAllRequestStatus,
  }
)(AssessmentByQuestionTypeBase);

export default AssessmentByQuestionType;
